// @flow
import Properties from "../../../Properties";
import { getInit, postInit, graphQLInit } from "../../global/types/constants";
import type { DispatchType } from "../../global/types/redux";
import {
  ProjectCompanyNode,
  ProjectNode,
  ProjectCloseStatusNode,
  CompanyInfoNode,
  LicenceNode
} from "../types/GraphQlNodes";
import { apiFailCallbackHandle } from "../containers/ErrosDisplay";
import {
  SET_RESELLER_TREE,
  UPDATE_PROJECT_LIST,
  GET_CLOSESTATUS_LIST,
  PROJECT_ENDUSER_LIST,
  ADMIN_UPDATE_PROJECT_LIST,
  GET_PROJECT_LIST,
  SET_DISTRIBUTER_TREE
} from "../../global/actions/actionNames";

const root = Properties.api_root;

export const fetchResellerTree = (callback?: Function): * => (
  dispatch: DispatchType
): * => {
  const graphQl = `
  {
    resellers { ${ProjectCompanyNode} }
  }
  `;
  return fetch(`${root}/auth/v2/project/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      dispatch({ type: SET_RESELLER_TREE, resellers: json.data.resellers });
      callback && callback(json.data.resellers);
    });
};

export const fetchDistributerTree = (callback?: Function): * => (
  dispatch: DispatchType
): * => {
  const graphQl = `
  {
    distributers { ${CompanyInfoNode} }
  }
  `;
  return fetch(`${root}/auth/v2/project/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      dispatch({ type: SET_DISTRIBUTER_TREE, distributers: json.data.distributers });
      callback && callback(json.data.distributers);
    });
};

export const fetcProjectList = (callback?: Function): * => (
  dispatch: DispatchType
): * => {
  const graphQl = `
  {
    projects {
      ${ProjectNode}
    }
  }
  `;
  return fetch(`${root}/auth/v2/project/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      dispatch({ type: GET_PROJECT_LIST, projects: json.data.projects });
      callback && callback(json.data.projects);
    });
};

export const fetcCloseStatusList = (callback?: Function): * => (
  dispatch: DispatchType
): * => {
  const graphQl = `
  {
    projectCloseStatuses {
      ${ProjectCloseStatusNode}
    }
  }
  `;
  return fetch(`${root}/auth/v2/project/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      dispatch({
        type: GET_CLOSESTATUS_LIST,
        closeStatuses: json.data.projectCloseStatuses
      });
      callback && callback(json.data.projectCloseStatuses);
    });
};

export const createProject = (
  name: string,
  closeDate: string,
  notes: string,
  noOfStreams: string,
  noOfFaces: string,
  address: string,
  suburb: string,
  city: string,
  state: string,
  country: string,
  postcode: string,
  resellerId: string,
  enduserId: string,
  shouldUpdateResellerTree: boolean,
  doneCallback: Function,
  failCallBack: Function
): * => (dispatch: DispatchType): * => {
  const streamNode =
    noOfStreams.trim() === "" ? "" : `noOfStreams: ${noOfStreams},`;
  const faceNode = noOfFaces.trim() === "" ? "" : `noOfFaces: ${noOfFaces},`;
  const graphQl = `
  mutation {
    createProject(
      name: "${name}",
      closeDate: "${closeDate}",
      notes: "${notes}",
      ${streamNode}
      ${faceNode}
      address: "${address}",
      suburb: "${suburb}",
      city: "${city}",
      state: "${state}",
      country: "${country}",
      postcode: "${postcode}",
      resellerId: "${resellerId}",
      enduserId: "${enduserId}"
    ){
      ok
      error {
        ... on ChildOfCompanyNotFound{
          message
          errorType
        }
        ... on ProjectAlreadyExists{
          message
          errorType
        }
      }
      project { ${ProjectNode} }
    }
  }
  `;
  return fetch(`${root}/auth/v2/project/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      if (json.errors) {
        failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
      } else {
        const result = json.data.createProject;
        if (result.ok) {
          dispatch({ type: UPDATE_PROJECT_LIST, project: result.project });
          if (shouldUpdateResellerTree) {
            dispatch(fetchResellerTree());
          }
          doneCallback && doneCallback();
        } else {
          failCallBack && failCallBack(apiFailCallbackHandle(result.error));
        }
      }
    })
    .catch(err => {
      failCallBack && failCallBack(apiFailCallbackHandle(err.message));
    });
};

export const updateProject = (
  project_id: string,
  name: string,
  closeDate: string,
  notes: string,
  noOfStreams: string,
  noOfFaces: string,
  address: string,
  suburb: string,
  city: string,
  state: string,
  country: string,
  postcode: string,
  resellerId: string,
  enduserId: string,
  doneCallback: Function,
  failCallBack: Function,
  isAdmin: boolean
): * => (dispatch: DispatchType): * => {
  const streamNode = noOfStreams === "" ? "" : `noOfStreams: ${noOfStreams},`;
  const faceNode = noOfFaces === "" ? "" : `noOfFaces: ${noOfFaces},`;
  const graphQl = `
  mutation {
    updateProject(
      projectId: "${project_id}",
      name: "${name}",
      closeDate: "${closeDate}",
      notes: "${notes}",
      ${streamNode}
      ${faceNode}
      address: "${address}",
      suburb: "${suburb}",
      city: "${city}",
      state: "${state}",
      country: "${country}",
      postcode: "${postcode}",
      resellerId: "${resellerId}",
      enduserId: "${enduserId}"
    ){
      ok
      error {
        ... on ChildOfCompanyNotFound{
          message
          errorType
        }
        ... on ProjectAlreadyExists{
          message
          errorType
        }
        ... on CanNotChangeEndUser{
          message
          errorType
        }
      }
      project { ${ProjectNode} }
    }
  }
  `;
  return fetch(`${root}/auth/v2/project/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      if (json.errors) {
        failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
      } else {
        const result = json.data.updateProject;
        if (result.ok) {
          if (isAdmin) {
            dispatch({
              type: ADMIN_UPDATE_PROJECT_LIST,
              project: result.project
            });
          } else {
            dispatch({ type: UPDATE_PROJECT_LIST, project: result.project });
          }
          doneCallback && doneCallback();
        } else {
          failCallBack && failCallBack(apiFailCallbackHandle(result.error));
        }
      }
    })
    .catch(err => {
      failCallBack && failCallBack(apiFailCallbackHandle(err.message));
    });
};

export const closeProject = (
  project_id: string,
  outcome: boolean,
  actualCloseDate: string,
  closeStatusId: string,
  closeStatusOther: string,
  isadmin: boolean,
  doneCallback: Function,
  failCallBack: Function
): * => (dispatch: DispatchType): * => {
  const graphQl = `
  mutation {
    closeProject(
      projectId: "${project_id}",
      outcome: ${outcome},
      actualCloseDate: "${actualCloseDate}",
      closeStatusId: "${closeStatusId}",
      closeStatusOther: "${closeStatusOther}"
    ){
      ok
      error {
        ... on ProjectNotFound{
          message
          errorType
        }
        ... on UserPermissionError{
          message
          errorType
        }
        ...on ProjectCloseReasonNotFound{
          message
          errorType
        }
        ...on LostReasonRequired {
          message
          errorType
        }
        ...on OtherReasonRequired{
          message
          errorType
        }
      }
      project { ${ProjectNode} }
    }
  }
  `;
  return fetch(`${root}/auth/v2/project/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      if (json.errors) {
        failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
      } else {
        const result = json.data.closeProject;
        if (result.ok) {
          if (isadmin) {
            dispatch({
              type: ADMIN_UPDATE_PROJECT_LIST,
              project: result.project
            });
          } else {
            dispatch({ type: UPDATE_PROJECT_LIST, project: result.project });
          }
          doneCallback && doneCallback();
        } else {
          failCallBack && failCallBack(apiFailCallbackHandle(result.error));
        }
      }
    })
    .catch(err => {
      failCallBack && failCallBack(apiFailCallbackHandle(err.message));
    });
};

export const linkQuote = (
  project_id: string,
  quote_ids: Array<string>,
  isadmin: boolean,
  doneCallback: Function,
  failCallBack: Function
): * => (dispatch: DispatchType): * => {
  const graphQl = `
  mutation {
    linkQuote(
      projectId: "${project_id}",
      quoteIds: ${JSON.stringify(quote_ids)}
    ){
      ok
      error {
        ...on ProjectNotFound{
          message
          errorType
        }
        ...on QuoteNotFound{
          message
          errorType
        }
        ...on UserPermissionError{
          message
          errorType
        }
      }
      project { ${ProjectNode} }
    }
  }
  `;
  return fetch(`${root}/auth/v2/project/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      if (json.errors) {
        failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
      } else {
        const result = json.data.linkQuote;
        if (result.ok) {
          if (isadmin) {
            dispatch({
              type: ADMIN_UPDATE_PROJECT_LIST,
              project: result.project
            });
          } else {
            dispatch({ type: UPDATE_PROJECT_LIST, project: result.project });
          }
          doneCallback && doneCallback();
        } else {
          failCallBack && failCallBack(apiFailCallbackHandle(result.error));
        }
      }
    })
    .catch(err => {
      failCallBack && failCallBack(apiFailCallbackHandle(err.message));
    });
};

export const fetchEndUserLicenceList = (callback?: Function): * => (
  dispatch: DispatchType
): * => {
  const graphQl = `
  {
    endusers {
      ${CompanyInfoNode}
      licences {
        edges {
          node {
            ${LicenceNode}
            project{
              id
            }
          }
        }
      }
    }
  }
  `;
  return fetch(`${root}/auth/v2/project/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      dispatch({ type: PROJECT_ENDUSER_LIST, endusers: json.data.endusers });
      callback && callback(json.data.endusers);
    });
};

export const linkLicence = (
  project_id: string,
  licence_id: string,
  isadmin: boolean,
  doneCallback: Function,
  failCallBack: Function
): * => (dispatch: DispatchType): * => {
  const graphQl = `
  mutation {
    linkLicence(
      projectId: "${project_id}",
      licenceId: "${licence_id}"
    ){
      ok
      error {
        ...on ProjectNotFound{
          message
          errorType
        }
        ...on LicenceNotFound{
          message
          errorType
        }
        ...on UserPermissionError{
          message
          errorType
        }
      }
      project { ${ProjectNode} }
    }
  }
  `;
  return fetch(`${root}/auth/v2/project/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      if (json.errors) {
        failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
      } else {
        const result = json.data.linkLicence;
        if (result.ok) {
          if (isadmin) {
            dispatch({
              type: ADMIN_UPDATE_PROJECT_LIST,
              project: result.project
            });
          } else {
            dispatch({ type: UPDATE_PROJECT_LIST, project: result.project });
          }
          doneCallback && doneCallback();
        } else {
          failCallBack && failCallBack(apiFailCallbackHandle(result.error));
        }
      }
    })
    .catch(err => {
      failCallBack && failCallBack(apiFailCallbackHandle(err.message));
    });
};
